import {
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  Snackbar,
  SnackbarCloseReason,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Clock, LocationCheck, Message, Pause, Phone, Play } from "../../icons";
import { ItemContact } from "../../components/ItemContact";
import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useLenis } from "../../hooks/useLenis";
import { isEmpty } from "../../utils/funtions";
import emailjs from "@emailjs/browser";
const initialData = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

export const Contact = () => {
  const theme = useTheme();
  useLenis(1);

  const boxVideoRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const [dataForm, setDataForm] = useState(initialData);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  const enableSubmit =
    dataForm.name && dataForm.email && dataForm.phone && dataForm.message;

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const [video, setVideo] = useState(true);
  const onSecondVideo = () => {
    if (videoRef.current) {
      if (video) {
        setVideo(false);
        videoRef.current.pause();
      } else {
        setVideo(true);
        videoRef.current.play();
      }
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (!titleRef.current) return;
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 90, transformPerspective: 1000, rotateX: 0.01 },
        {
          opacity: 1,
          y: 0,
          ease: "power1.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top top+=200",
            end: "bottom bottom",
            once: true,
          },
        }
      );

      if (!boxVideoRef.current) return;
      gsap.fromTo(
        boxVideoRef.current,
        { scale: 1, borderRadius: 0 },
        {
          scale: 0.95,
          ease: "none",
          borderRadius: 30,

          scrollTrigger: {
            trigger: boxVideoRef.current,
            start: "top top+=150",
            end: "+=200",
            scrub: 0.5,
          },
        }
      );
    });
    return () => ctx.revert();
  }, []);

const onContact = async (e: React.FormEvent) => {
  e.preventDefault();

  if (
    isEmpty(dataForm.name) ||
    isEmpty(dataForm.email) ||
    isEmpty(dataForm.phone) ||
    isEmpty(dataForm.message)
  ) {
    setOpen(true);
    setMessage("Please complete all required fields");
    return;
  }

  try {
    await emailjs.send(
      "service_gqq2fjr",
      "template_rht1q38",
      {
        name: dataForm.name,
        email: dataForm.email,
        phone: dataForm.phone,
        message: dataForm.message,
      },
      "lQm1PT1WrHbfCqhGs"
    );

    setMessage("Message sent successfully");
    setOpen(true);
    setDataForm(initialData);
  } catch (error) {
    console.error(error);
    setMessage("Error sending message");
    setOpen(true);
  }
};
  return (
    <Box>
      <Box
        ref={titleRef}
        marginBlock={8}
        width="100%"
        display="flex"
        flexDirection={{ sm: "row", xs: "column" }}
        justifyContent={{ sm: "space-between" }}
        alignItems={{ xs: "flex-start" }}
      >
        <Typography
          sx={{
            marginInline: { xs: 3, md: 8 },
            fontSize: { xs: 30, sm: 35, md: 40 },
            fontWeight: 800,
            textAlign: "center",
            background: `linear-gradient(50deg, ${theme.palette.secondary[900]} , ${theme.palette.secondary[300]} 70%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Get in touch
        </Typography>
        <Typography
          sx={{
            marginInline: { xs: 3, md: 8 },
            fontSize: { xs: 15, sm: 20, md: 25 },
            fontWeight: 600,
            maxWidth: 200,
            textAlign: "center",
            color: theme.palette.secondary[100],
          }}
        >
          We’d love to help you.
        </Typography>
      </Box>

      <Box
        position="relative"
        ref={boxVideoRef}
        width="100%"
        overflow="hidden"
        height={{ xs: 250, sm: 450, md: 650 }}
      >
        <Fade in timeout={1500}>
          <video
            ref={videoRef}
            poster="https://yzlbdstwuaznspepvtyo.supabase.co/storage/v1/object/public/nikolay/contact/poster.jpg"
            src="https://yzlbdstwuaznspepvtyo.supabase.co/storage/v1/object/public/nikolay/contact/contact.mp4"
            autoPlay={true}
            loop={true}
            muted
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Fade>

        <Box
          position="absolute"
          bottom={{ xs: 20, md: 60 }}
          zIndex={99}
          right={{ xs: 20, md: 60 }}
        >
          <IconButton
            onClick={onSecondVideo}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: { xs: 30, md: 40 },
              height: { xs: 30, md: 40 },
              backdropFilter: "saturate(180%) blur(5px)",
              backgroundColor: theme.palette.background.paper,
              borderRadius: "100vmax !important",
              "&:hover": {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            {!video ? (
              <Play
                sx={{ width: { xs: 15, md: 19 }, height: { xs: 15, md: 19 } }}
              />
            ) : (
              <Pause
                sx={{ width: { xs: 15, md: 19 }, height: { xs: 15, md: 19 } }}
              />
            )}
          </IconButton>
        </Box>
      </Box>

      <Grid
        display="flex"
        flexDirection={{ xs: "column-reverse", md: "row" }}
        justifyContent="space-between"
        spacing={{ md: 8, xs: 25 }}
        marginBlock={{ xs: 10, md: 20 }}
        container
        padding="30px 20px"
        marginInline={{ xs: 3, md: 6 }}
        borderRadius={3}
      >
        <Grid
          display="flex"
          flexDirection="column"
          position="relative"
          gap={7}
          size={{ xs: 12, md: 4 }}
        >
          <ItemContact
            icon={
              <LocationCheck
                sx={{ width: 20, height: 20, mt: 1 }}
                htmlColor={theme.palette.primary[500]}
              />
            }
            title="Visit us"
            href="https://maps.app.goo.gl/D8uZTrSk3DTkJLGX9"
            labelLink="7003 Presidents Drive Unit 600, Orlando, FL, 32809"
          />
          <ItemContact
            icon={
              <Phone
                sx={{ width: 20, height: 20, mt: 1 }}
                htmlColor={theme.palette.primary[500]}
              />
            }
            title="Call us"
            notTarget
            href="tel:+13212434480"
            labelLink="+ 1 (321) 243-4480"
          />
          <ItemContact
            icon={
              <Message
                sx={{ width: 20, height: 20, mt: 1 }}
                htmlColor={theme.palette.primary[500]}
              />
            }
            title="Talk to us"
            href="mailto:shop@nikolayorlando.com"
            labelLink="shop@nikolayorlando.com"
          />
          <ItemContact
            icon={
              <Clock
                sx={{ width: 20, height: 20, mt: 1 }}
                htmlColor={theme.palette.primary[500]}
              />
            }
            title="Working hours"
            label="Mon-Fri: 10 am — 6 pm | Sat: 10 am — 3 pm (Closed Sun)"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Grid component="form" onSubmit={onContact} spacing={4} container>
            <Grid size={12}>
              <Typography
                variant="h4"
                sx={{ color: theme.palette.secondary[500] }}
              >
                Drop us a line
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="name"
                id="name"
                required
                value={dataForm.name}
                onChange={({ target }) =>
                  setDataForm((d) => ({ ...d, name: target.value }))
                }
                type="text"
                label="Name"
                fullWidth
                sx={{ color: theme.palette.secondary[200] }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                required
                type="email"
                label="Email"
                name="email"
                id="email"
                value={dataForm.email}
                onChange={({ target }) =>
                  setDataForm((d) => ({ ...d, email: target.value }))
                }
                fullWidth
                sx={{ color: theme.palette.secondary[200] }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                required
                type="tel"
                label="Phone number"
                name="phone"
                id="phone"
                value={dataForm.phone}
                onChange={({ target }) =>
                  setDataForm((d) => ({ ...d, phone: target.value }))
                }
                fullWidth
                sx={{ color: theme.palette.secondary[200] }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                required
                type="text"
                fullWidth
                multiline
                id="message"
                name="message"
                value={dataForm.message}
                onChange={({ target }) =>
                  setDataForm((d) => ({ ...d, message: target.value }))
                }
                rows={3}
                label="Message"
                sx={{ color: theme.palette.secondary[200] }}
              />
            </Grid>
            <Grid size={12}>
              <Button
                disabled={!enableSubmit}
                type="submit"
                sx={{ padding: "10px 15px" }}
                variant="contained"
                color="secondary"
                disableElevation
              >
                Send request
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={3000}
        onClose={handleClose}
        ContentProps={{
          sx: {
            boxShadow: "none",
            borderRadius: 2,
            backgroundColor: `${theme.palette.background.paper} !important`,
            backdropFilter: "saturate(10%) blur(5px)",
            color:
              message === "Invalid email"
                ? theme.palette.error.main
                : message === "This email is already registered."
                  ? theme.palette.warning.main
                  : theme.palette.success.main,
          },
        }}
        open={open}
        message={message}
      />
    </Box>
  );
};
